import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { propertyService } from "services/firestoreService";
import { useAuth } from "contexts/AuthContext";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";

// Icons
import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HomeIcon from "@mui/icons-material/Home";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

function AddProperty() {
  // Hooks
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [propertyType, setPropertyType] = useState("residential");
  const [propertyStatus, setPropertyStatus] = useState("forSale");
  const [amenities, setAmenities] = useState({
    pool: false,
    garden: false,
    garage: false,
    security: false,
    airConditioning: false,
    heating: false,
    furnished: false,
    petFriendly: false,
  });
  const [images, setImages] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const steps = [
    "Basic Information",
    "Location & Details",
    "Amenities & Features",
    "Images & Documents",
    "Review & Submit",
  ];

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      // Submit the form
      try {
        const propertyData = {
          name: "New Property",
          address: "123 Example St",
          type: propertyType,
          status: "Active",
          value: "$500,000",
          roi: "6.5%",
          occupancy: "100%",
          initial: "N"
        };

        const docRef = await propertyService.create(propertyData, user.uid);

        console.log('Property created successfully:', docRef);
        setOpenSnackbar(true);
        // Redirect after showing success message
        setTimeout(() => {
          navigate('/properties/all');
        }, 2000);
      } catch (error) {
        console.error('Error adding property:', error);
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAmenityChange = (event) => {
    setAmenities({
      ...amenities,
      [event.target.name]: event.target.checked,
    });
  };

  const handleFileChange = (event) => {
    if (event.target.files) {
      setImages([...images, ...Array.from(event.target.files)]);
    }
  };

  const handleDocumentChange = (event) => {
    if (event.target.files) {
      // Handle document uploads - you can add similar state management as with images
      console.log("Documents selected:", Array.from(event.target.files));
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={2}>
        <Card sx={{ padding: 2 }}>
          <ArgonBox mb={2}>
            <ArgonTypography variant="h5" fontWeight="medium">
              Add New Property
            </ArgonTypography>
            <ArgonTypography variant="body2" color="text" mt={0.5} sx={{ fontSize: '0.85rem' }}>
              Fill out the form below to add a new property to your portfolio
            </ArgonTypography>
          </ArgonBox>

          <ArgonBox mb={3}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </ArgonBox>

          {activeStep === 0 && (
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" mb={2}>
                Basic Information
              </ArgonTypography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Property Title
                    </ArgonTypography>
                    <ArgonInput
                      placeholder="e.g., Modern Beachfront Villa"
                      fullWidth
                    />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      BBL (Borough, Block, Lot)
                    </ArgonTypography>
                    <ArgonInput placeholder="e.g., 3-00123-0045" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Property Type
                    </ArgonTypography>
                    <FormControl fullWidth>
                      <Select
                        value={propertyType}
                        onChange={(e) => setPropertyType(e.target.value)}
                        displayEmpty
                        inputProps={{ "aria-label": "Property Type" }}
                      >
                        <MenuItem value="residential">Residential</MenuItem>
                        <MenuItem value="commercial">Commercial</MenuItem>
                        <MenuItem value="industrial">Industrial</MenuItem>
                        <MenuItem value="land">Land</MenuItem>
                      </Select>
                    </FormControl>
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Status
                    </ArgonTypography>
                    <FormControl fullWidth>
                      <Select
                        value={propertyStatus}
                        onChange={(e) => setPropertyStatus(e.target.value)}
                        displayEmpty
                        inputProps={{ "aria-label": "Property Status" }}
                      >
                        <MenuItem value="forSale">For Sale</MenuItem>
                        <MenuItem value="forRent">For Rent</MenuItem>
                        <MenuItem value="sold">Sold</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                      </Select>
                    </FormControl>
                  </ArgonBox>
                </Grid>
              </Grid>
            </ArgonBox>
          )}

          {activeStep === 1 && (
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" mb={2}>
                Location & Details
              </ArgonTypography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Address
                    </ArgonTypography>
                    <ArgonInput placeholder="Street Address" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      City
                    </ArgonTypography>
                    <ArgonInput placeholder="City" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      State/Province
                    </ArgonTypography>
                    <ArgonInput placeholder="State/Province" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Postal Code
                    </ArgonTypography>
                    <ArgonInput placeholder="Postal Code" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Country
                    </ArgonTypography>
                    <ArgonInput placeholder="Country" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Bedrooms
                    </ArgonTypography>
                    <ArgonInput type="number" placeholder="0" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={3}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Bathrooms
                    </ArgonTypography>
                    <ArgonInput type="number" placeholder="0" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={3}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Total Area (sq ft)
                    </ArgonTypography>
                    <ArgonInput type="number" placeholder="0" fullWidth />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={3}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Year Built
                    </ArgonTypography>
                    <ArgonInput type="number" placeholder="2023" fullWidth />
                  </ArgonBox>
                </Grid>
              </Grid>
            </ArgonBox>
          )}

          {activeStep === 2 && (
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" mb={2}>
                Amenities & Features
              </ArgonTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities.pool}
                        onChange={handleAmenityChange}
                        name="pool"
                      />
                    }
                    label="Swimming Pool"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities.garden}
                        onChange={handleAmenityChange}
                        name="garden"
                      />
                    }
                    label="Garden"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities.garage}
                        onChange={handleAmenityChange}
                        name="garage"
                      />
                    }
                    label="Garage"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities.security}
                        onChange={handleAmenityChange}
                        name="security"
                      />
                    }
                    label="Security System"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities.airConditioning}
                        onChange={handleAmenityChange}
                        name="airConditioning"
                      />
                    }
                    label="Air Conditioning"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities.heating}
                        onChange={handleAmenityChange}
                        name="heating"
                      />
                    }
                    label="Heating System"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities.furnished}
                        onChange={handleAmenityChange}
                        name="furnished"
                      />
                    }
                    label="Furnished"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities.petFriendly}
                        onChange={handleAmenityChange}
                        name="petFriendly"
                      />
                    }
                    label="Pet Friendly"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Property Description
                    </ArgonTypography>
                    <FormControl fullWidth>
                      <OutlinedInput
                        placeholder="Describe your property in detail..."
                        multiline
                        rows={4}
                      />
                    </FormControl>
                  </ArgonBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Price ($)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      placeholder="Enter price"
                      fullWidth
                      icon={{ component: <AttachMoneyIcon />, direction: "left" }}
                    />
                  </ArgonBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <ArgonBox mb={2}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Property Tags
                    </ArgonTypography>
                    <ArgonInput
                      placeholder="e.g., Luxury, Waterfront, New"
                      fullWidth
                    />
                  </ArgonBox>
                </Grid>
              </Grid>
            </ArgonBox>
          )}

          {activeStep === 3 && (
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" mb={2}>
                Images & Documents
              </ArgonTypography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ArgonBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                      border: "1px dashed #ccc",
                      borderRadius: "10px",
                      padding: 3,
                      backgroundColor: "#f8f9fa",
                      cursor: "pointer",
                    }}
                    component="label"
                  >
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                    <AddPhotoAlternateIcon
                      sx={{ fontSize: 60, color: "#5e72e4", mb: 2 }}
                    />
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Click to select images
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="text">
                      Supported formats: JPG, PNG, WEBP (Max 5MB each)
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>

                {images.length > 0 && (
                  <Grid item xs={12}>
                    <ArgonTypography variant="h6" fontWeight="medium" mt={2} mb={1}>
                      Uploaded Files ({images.length})
                    </ArgonTypography>
                    <Grid container spacing={2}>
                      {images.map((file, index) => (
                        <Grid item xs={6} md={3} key={index}>
                          <Card sx={{ height: "100px", position: "relative" }}>
                            <ArgonBox
                              sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 1,
                              }}
                            >
                              <ArgonTypography variant="body2" noWrap>
                                {file.name}
                              </ArgonTypography>
                              <IconButton
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 5,
                                  right: 5,
                                }}
                                onClick={() => {
                                  const newImages = [...images];
                                  newImages.splice(index, 1);
                                  setImages(newImages);
                                }}
                              >
                                <Icon>close</Icon>
                              </IconButton>
                            </ArgonBox>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                    Additional Documents
                  </ArgonTypography>
                  <ArgonBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                      border: "1px dashed #ccc",
                      borderRadius: "10px",
                      padding: 3,
                      backgroundColor: "#f8f9fa",
                      cursor: "pointer",
                    }}
                    component="label"
                  >
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx"
                      onChange={handleDocumentChange}
                      style={{ display: "none" }}
                    />
                    <Icon sx={{ fontSize: 60, color: "#5e72e4", mb: 2 }}>
                      description
                    </Icon>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Click to upload property documents
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="text">
                      Floor plans, certificates, titles, etc. (PDF, DOC, DOCX)
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>
              </Grid>
            </ArgonBox>
          )}

          {activeStep === 4 && (
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" mb={2}>
                Review & Submit
              </ArgonTypography>
              <Card sx={{ padding: 3, backgroundColor: "#f8f9fa", mb: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      sx={{ backgroundColor: "#e9ecef", p: 2, borderRadius: 1 }}
                    >
                      <CheckCircleIcon sx={{ color: "green", mr: 1 }} />
                      <ArgonTypography variant="body2" fontWeight="medium">
                        All required information has been provided
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Property Summary
                    </ArgonTypography>
                    <ArgonBox mt={2}>
                      <ArgonTypography variant="body2" color="text">
                        <strong>Title:</strong> Modern Beachfront Villa
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text">
                        <strong>BBL:</strong> 3-00123-0045
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text">
                        <strong>Status:</strong> For Sale
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text">
                        <strong>Price:</strong> $1,250,000
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text">
                        <strong>Location:</strong> 123 Ocean Drive, Miami, FL
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text">
                        <strong>Area:</strong> 2,500 sq ft
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text">
                        <strong>Bedrooms:</strong> 4
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="text">
                        <strong>Bathrooms:</strong> 3
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="h6" fontWeight="medium">
                      Selected Amenities
                    </ArgonTypography>
                    <ArgonBox mt={2}>
                      <Grid container spacing={1}>
                        {Object.entries(amenities)
                          .filter(([_, value]) => value)
                          .map(([key]) => (
                            <Grid item xs={6} key={key}>
                              <ArgonBox
                                display="flex"
                                alignItems="center"
                                sx={{ mb: 1 }}
                              >
                                <CheckCircleIcon
                                  sx={{ color: "green", mr: 1, fontSize: 16 }}
                                />
                                <ArgonTypography variant="body2" color="text">
                                  {key.charAt(0).toUpperCase() +
                                    key.slice(1).replace(/([A-Z])/g, " $1")}
                                </ArgonTypography>
                              </ArgonBox>
                            </Grid>
                          ))}
                      </Grid>
                    </ArgonBox>
                    <ArgonTypography variant="h6" fontWeight="medium" mt={2}>
                      Uploads
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="text" mt={1}>
                      {images.length} images uploaded
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="text">
                      0 documents uploaded
                    </ArgonTypography>
                  </Grid>
                </Grid>
              </Card>
              <ArgonBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <ArgonTypography variant="body2" color="text">
                  By submitting this property, you confirm that all information is
                  accurate and you have the rights to list this property.
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
          )}

          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={4}
          >
            <ArgonButton
              variant="contained"
              color="secondary"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="info"
              onClick={handleNext}
              startIcon={
                activeStep === steps.length - 1 ? (
                  <CheckCircleIcon />
                ) : (
                  <AddIcon />
                )
              }
            >
              {activeStep === steps.length - 1 ? "Submit Property" : "Next"}
            </ArgonButton>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Property successfully added! Redirecting to property list...
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default AddProperty;